import React, { useContext, useState } from "react";
import "./Signin.css";
import axios from "axios";
import { AuthContext } from "../Context/AuthContext.js";
import Switch from "@material-ui/core/Switch";
import { CircularProgress } from "@material-ui/core";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import Brightness5Icon from "@material-ui/icons/Brightness5";
import Brightness2Icon from "@material-ui/icons/Brightness2";
import WarningIcon from "@material-ui/icons/Warning";
import config from "../config";
import { Badge, Button, Card, Input } from "../ui";

function Signin() {
    const [isStaff, setIsStaff] = useState(false);
    const [admissionId, setAdmissionId] = useState("");
    const [employeeId, setEmployeeId] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const { dispatch } = useContext(AuthContext);

    const API_URL = config.API_URL;

    const loginCall = async (userCredential) => {
        setLoading(true);
        dispatch({ type: "LOGIN_START" });
        try {
            const res = await axios.post(API_URL + "api/auth/signin", userCredential);

            const userType = res.data.userType.toLowerCase();

            if (isStaff && !["staff", "employee", "admin"].includes(userType)) {
                setError("This account does not have staff privileges");
                dispatch({ type: "LOGIN_FAILURE", payload: "Invalid role" });
                setLoading(false);
                return;
            }

            if (!isStaff && !["student"].includes(userType)) {
                setError("This account does not have student privileges");
                dispatch({ type: "LOGIN_FAILURE", payload: "Invalid role" });
                setLoading(false);
                return;
            }

            dispatch({ type: "LOGIN_SUCCESS", payload: res.data });
            setLoading(false);
        } catch (err) {
            dispatch({ type: "LOGIN_FAILURE", payload: err });
            setError(err.response?.data || "Wrong password or username");
            setLoading(false);
        }
    };

    const handleForm = (event) => {
        event.preventDefault();
        setError("");

        if (isStaff) {
            if (!employeeId) {
                setError("Employee ID is required");
                return;
            }
            loginCall({ employeeId, password });
        } else {
            if (!admissionId) {
                setError("Admission ID is required");
                return;
            }
            loginCall({ admissionId, password });
        }
    };

    const handleModeToggle = () => {
        setIsStaff((prev) => !prev);
        setError("");
        setEmployeeId("");
        setAdmissionId("");
        setPassword("");
    };

    return (
        <div className="signin">
            <div className="signin__orbital" />
            <div className="signin__grid">
                <div className="signin__intro">
                    <Badge variant="primary">Library Command</Badge>
                    <h1>Access the Library Intelligence Suite</h1>
                    <p>
                        A premium control center crafted for librarians, staff, and students
                        to orchestrate reservations, collections, and knowledge experiences.
                    </p>
                    <div className="signin__summary">
                        <div>
                            <strong>Supreme UX</strong>
                            <span>Luxury-grade interaction patterns, consistent on every device.</span>
                        </div>
                        <div>
                            <strong>Adaptive Theme</strong>
                            <span>Shape the brand aesthetic in real time with the design studio.</span>
                        </div>
                    </div>
                </div>

                <Card className="signin__card" padding="lg" interactive>
                    <Badge variant="primary">
                        {isStaff ? "Staff Command Deck" : "Student Portal"}
                    </Badge>
                    <div className="signin__heading">
                        <h2>Welcome back</h2>
                        <p>
                            Authenticate with your institutional credentials to enter the
                            library experience tailored just for you.
                        </p>
                    </div>
                    <form className="signin__form" onSubmit={handleForm}>
                        <div className="signin__toggle">
                            <div>
                                <span className="signin__toggle-label">Staff access</span>
                                <p>
                                    {isStaff
                                        ? "Switching off returns to student authentication."
                                        : "Enable to authenticate with staff credentials."}
                                </p>
                            </div>
                            <div className="signin__toggle-action">
                                <Brightness5Icon className="signin__toggle-icon" />
                                <Switch
                                    checked={isStaff}
                                    onChange={handleModeToggle}
                                    color="primary"
                                />
                                <Brightness2Icon className="signin__toggle-icon" />
                            </div>
                        </div>

                        {error && (
                            <div className="signin__alert">
                                <WarningIcon />
                                <span>{error}</span>
                            </div>
                        )}

                        <Input
                            id={isStaff ? "employeeId" : "admissionId"}
                            label={isStaff ? "Employee ID" : "Admission ID"}
                            value={isStaff ? employeeId : admissionId}
                            placeholder={
                                isStaff ? "Enter employee credential" : "Enter admission ID"
                            }
                            onChange={(event) => {
                                if (isStaff) {
                                    setEmployeeId(event.target.value);
                                } else {
                                    setAdmissionId(event.target.value);
                                }
                                setError("");
                            }}
                            required
                        />

                        <Input
                            id="password"
                            label="Secure Password"
                            type="password"
                            minLength={6}
                            value={password}
                            placeholder="Enter password"
                            onChange={(event) => {
                                setPassword(event.target.value);
                                setError("");
                            }}
                            required
                        />

                        <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            disabled={loading}
                            trailingIcon={!loading ? <ArrowForwardIcon fontSize="small" /> : null}
                        >
                            {loading ? (
                                <CircularProgress
                                    size={20}
                                    style={{ color: "var(--color-on-primary)" }}
                                />
                            ) : (
                                "Enter Library"
                            )}
                        </Button>
                    </form>

                    <div className="signin__footer">
                        <a href="#forgot">Forgot password?</a>
                        <div className="signin__footnote">
                            <LockOutlinedIcon />
                            <span>
                                {isStaff
                                    ? "Need a staff account? Contact the library administrator."
                                    : "Students receive credentials from the librarian desk."}
                            </span>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}

export default Signin;